import { useContext } from 'react';
import { AlertDialogContext } from './AlertDialogProvider';
import { Alert, Platform } from 'react-native';

export const useAlertDialog = () => {
  const context = useContext(AlertDialogContext);

  // If the provider is missing (e.g. during isolated tests, storybook, or
  // partial renders), fall back to platform-native alerts instead of throwing
  // and breaking the entire UI tree.
  if (!context || !context.showAlert || !context.showConfirm) {
    console.warn(
      '[useAlertDialog] AlertDialogProvider not found in tree – ' +
        'falling back to native alerts. Wrap your app in <AlertDialogProvider> ' +
        'for consistent styling and behavior.'
    );

    const fallbackShowAlert = async (title: string, message?: string) => {
      if (Platform.OS === 'web') {
        // eslint-disable-next-line no-alert
        window.alert(message ? `${title}\n\n${message}` : title);
      } else {
        Alert.alert(title, message);
      }
    };

    const fallbackShowConfirm = async (
      title: string,
      message?: string
    ): Promise<boolean> => {
      if (Platform.OS === 'web') {
        // eslint-disable-next-line no-alert
        return window.confirm(message ? `${title}\n\n${message}` : title);
      }

      return new Promise<boolean>((resolve) => {
        Alert.alert(
          title,
          message,
          [
            { text: 'Cancel', style: 'cancel', onPress: () => resolve(false) },
            { text: 'OK', onPress: () => resolve(true) },
          ],
          { cancelable: true }
        );
      });
    };

    return {
      showAlert: fallbackShowAlert,
      showConfirm: fallbackShowConfirm,
    };
  }

  return context;
};
